import React, { FormEvent, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import {
  Box,
  Switch,
  Alert,
  Text,
  Heading,
  ChipGroup,
  Chip,
  Card,
  CardBody,
  Dropdown,
  SelectInput,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  Button,
  Link,
  ArrowRightIcon,
  ChevronLeftIcon,
  BulkPayoutsIcon,
  InvoicesIcon,
  SettlementsIcon,
  ChevronsRightIcon,
  useToast,
} from '@razorpay/blade/components';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { fetchLoggedInUser } from 'reducers/loggedInUser';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { PayrollInfoRequest } from 'api/companyOnboarding';
import { trackSegment } from 'utils/segment';
import { trackGtag, trackRazorpayAnalytics } from 'utils/razorpayAnalytics';
import { ordinalSuffix } from 'utils/Numbers';
import { handleBackToSetupGuide, getPayrollDateBanner } from '../utils';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import IsFeatureDisabledForRearch from 'access/IsFeatureDisabledForRearch';
import { StyledForm } from './styles';

const PRESET_DATES = [1, 5, 15, 31];

const payrollDates: { key: number; value: number }[] = [];
for (var i = 1; i <= 31; i++) {
  payrollDates.push({ key: i, value: i });
}

const PayrollDate = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isPayrollAutoPilotDisabled, isReArchEmployeeLoansDisabled } =
    useIsFeatureDisabledForRearch();
  const currentOrganization = useSelector(loggedInUserSelectors.currentOrganization);

  const savedPayrollDate = currentOrganization?.payrollDate ?? 1;
  const [payrollDate, setPayrollDate] = useState(savedPayrollDate);
  const [isCustomDate, setIsCustomDate] = useState(!PRESET_DATES.includes(savedPayrollDate));
  const [isPayrollEnabled, setIsPayrollEnabled] = useState(!!currentOrganization?.isPayrollEnabled);
  const [isAutoPayrollEnabled, setIsAutoPayrollEnabled] = useState(
    !!currentOrganization?.autoExecutePayroll,
  );
  const [isAdvanceSalaryEnabled, setIsAdvanceSalaryEnabled] = useState(
    !!currentOrganization?.advanceSalaryEnabled,
  );
  const [isLoanRequestsEnabled, setIsLoanRequestsEnabled] = useState(false);

  const { isLoading: isLoanSettingsLoading, isError: isLoanSettingsError } = useQuery({
    queryKey: ['LOAN_SETTINGS_ONBOARDING'],
    queryFn: api.loans.getLoanSettings,
    enabled: !isReArchEmployeeLoansDisabled,
    onSuccess: (response) => {
      setIsLoanRequestsEnabled(response.is_loan_requests_enabled);
    },
  });

  const { data: loanCategoriesData, isLoading: isLoanCategoriesLoading } = useQuery({
    queryKey: ['LOAN_CATEGORIES_ONBOARDING'],
    queryFn: () => api.loans.listLoanCategories(),
    enabled: !isReArchEmployeeLoansDisabled && isLoanRequestsEnabled,
    staleTime: 5 * 60 * 1000,
  });
  const loanCategoryCount = loanCategoriesData?.data?.length ?? 0;

  const updateLoanSettingsMutation = useMutation(
    (enabled: boolean) => api.loans.updateLoanSettings({ is_loan_requests_enabled: enabled }),
    {
      onError: (error: typeof AppError) => {
        const { messageForUser } = getAppErrorDetails(error);
        toast.show({
          content: messageForUser || 'Failed to update loan settings. Please try again.',
          color: 'negative',
        });
      },
    },
  );

  const resetChildFields = () => {
    setPayrollDate(1);
    setIsCustomDate(false);
    setIsAutoPayrollEnabled(!!currentOrganization?.autoExecutePayroll);
    setIsAdvanceSalaryEnabled(!!currentOrganization?.advanceSalaryEnabled);
  };

  const updatePayrollInfoMutation = useMutation(
    ({
      isPayrollEnabled,
      payrollDate,
      isAutoPayrollEnabled,
      isAdvanceSalaryEnabled,
    }: PayrollInfoRequest) =>
      api.companyOnboarding.updatePayrollInfo({
        isPayrollEnabled,
        payrollDate,
        isAutoPayrollEnabled,
        isAdvanceSalaryEnabled,
      }),
    {
      onSuccess: () => {
        trackSegment('onboarding.company_details.set_payroll_date.continue_click');
        triggerOnboardingPixels();
        dispatch(fetchLoggedInUser());
        history.push(routePaths.companyOnboarding.salaryStructure);
      },
    },
  );

  const triggerOnboardingPixels = () => {
    trackRazorpayAnalytics('fb', 'Set_Payroll_Date');
    trackRazorpayAnalytics('linkedin_onboarding_payroll_date', { conversion_id: 7377300 });
    trackRazorpayAnalytics('twitter_onboarding_payroll_date', {
      tw_sale_amount: 0,
      tw_order_quantity: 0,
    });
    trackGtag('event', 'conversion', { send_to: 'AW-928471290/TimvCOzhx7IDEPqx3boD' });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isReArchEmployeeLoansDisabled) {
      updateLoanSettingsMutation.mutate(isLoanRequestsEnabled);
    }
    updatePayrollInfoMutation.mutate({
      isPayrollEnabled,
      payrollDate,
      isAutoPayrollEnabled,
      isAdvanceSalaryEnabled,
    });
  };

  const handlePresetPillClick = (date: number) => {
    setPayrollDate(date);
    setIsCustomDate(false);
  };

  const isFormBeingSubmitted = updatePayrollInfoMutation.status === 'loading';
  const isPayrollAutopilotEnabled = isPayrollAutoPilotDisabled
    ? false
    : (currentOrganization?.noOfEmployees || 0) < 500;

  return (
    <Box maxWidth="640px" marginX="auto">
      <StyledForm id="payroll-date-form" onSubmit={handleSubmit}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom="spacing.6">
          <Box>
            <Heading
              size="medium"
              color="surface.text.gray.normal"
              testID="onboarding-enable-payroll-heading">
              Enable Payroll
            </Heading>
            <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
              Run payroll through RazorpayX
            </Text>
          </Box>
          <Switch
            accessibilityLabel="Enable Payroll"
            isChecked={isPayrollEnabled}
            onChange={({ isChecked }) => {
              if (isChecked && !isPayrollEnabled) {
                resetChildFields();
              }
              setIsPayrollEnabled(isChecked);
            }}
            isDisabled={isFormBeingSubmitted}
          />
        </Box>

        {isPayrollEnabled && (
          <>
            <Box marginBottom="spacing.5">
              <Card padding="spacing.7">
                <CardBody>
                  <Heading
                    size="small"
                    color="surface.text.gray.normal"
                    testID="onboarding-when-to-pay-heading">
                    When would you like to pay your employees?
                  </Heading>
                  <Text
                    size="small"
                    color="surface.text.gray.muted"
                    marginTop="spacing.2"
                    textAlign="left">
                    Pick a date after the 15th to pay in the same month, or on/before the 15th to
                    pay at the start of the next month.
                  </Text>

                  <Box marginTop="spacing.4" marginBottom="spacing.4">
                    <ChipGroup
                      accessibilityLabel="Pay date"
                      selectionType="single"
                      value={isCustomDate ? 'custom' : String(payrollDate)}
                      onChange={({ values }) => {
                        const value = values[0];
                        if (value === 'custom') {
                          setIsCustomDate(true);
                        } else if (value) {
                          handlePresetPillClick(Number(value));
                        }
                      }}
                      isDisabled={isFormBeingSubmitted}>
                      <Chip value="1">1st</Chip>
                      <Chip value="5">5th</Chip>
                      <Chip value="15">15th</Chip>
                      <Chip value="31">Last day</Chip>
                      <Chip value="custom">Custom</Chip>
                    </ChipGroup>
                  </Box>

                  {isCustomDate && (
                    <Box marginBottom="spacing.4">
                      <Dropdown selectionType="single">
                        <SelectInput
                          label="Day of month"
                          placeholder="Select a day (1–31)"
                          value={String(payrollDate)}
                          onChange={({ values }) => {
                            if (values[0]) {
                              setPayrollDate(Number(values[0]));
                            }
                          }}
                          isDisabled={isFormBeingSubmitted}
                        />
                        <DropdownOverlay>
                          <ActionList>
                            {payrollDates.map(({ key, value }) => (
                              <ActionListItem
                                key={key}
                                title={value === 31 ? 'Last day (31st)' : ordinalSuffix(value)}
                                value={String(value)}
                              />
                            ))}
                          </ActionList>
                        </DropdownOverlay>
                      </Dropdown>
                    </Box>
                  )}

                  <Alert
                    color="information"
                    emphasis="subtle"
                    isDismissible={false}
                    description={
                      <Text size="small" textAlign="left">
                        {getPayrollDateBanner(payrollDate)}
                      </Text>
                    }
                  />
                </CardBody>
              </Card>
            </Box>

            {isPayrollAutopilotEnabled && (
              <Box marginBottom="spacing.5">
                <Card padding="spacing.7">
                  <CardBody>
                    <Heading
                      size="small"
                      color="surface.text.gray.normal"
                      testID="onboarding-auto-run-payroll-heading">
                      Run payroll automatically each month?
                    </Heading>
                    {/* alignItems="stretch" + height="100%" on Card keeps both cards equal
                        height with no double-stroke artifact on the selected one. */}
                    <Box display="flex" gap="spacing.7" marginTop="spacing.5" alignItems="stretch">
                      <Box flex={1}>
                        <Card
                          width="100%"
                          height="100%"
                          isSelected={isAutoPayrollEnabled === true}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!isFormBeingSubmitted) setIsAutoPayrollEnabled(true);
                          }}
                          padding="spacing.7"
                        >
                          <CardBody>
                            <Box display="flex" flexDirection="column" gap="spacing.5">
                              <BulkPayoutsIcon color="surface.icon.gray.normal" size="medium" />
                              <Box display="flex" flexDirection="column" gap="spacing.1">
                                <Text
                                  weight="semibold"
                                  size="medium"
                                  color="surface.text.gray.normal"
                                  textAlign="left">
                                  Automatic
                                </Text>
                                <Text size="small" color="surface.text.gray.muted" textAlign="left">
                                  Runs on your chosen date every month. No action needed.
                                </Text>
                              </Box>
                            </Box>
                          </CardBody>
                        </Card>
                      </Box>
                      <Box flex={1}>
                        <Card
                          width="100%"
                          height="100%"
                          isSelected={isAutoPayrollEnabled === false}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!isFormBeingSubmitted) setIsAutoPayrollEnabled(false);
                          }}
                          padding="spacing.7"
                        >
                          <CardBody>
                            <Box display="flex" flexDirection="column" gap="spacing.5">
                              <InvoicesIcon color="surface.icon.gray.normal" size="medium" />
                              <Box display="flex" flexDirection="column" gap="spacing.1">
                                <Text
                                  weight="semibold"
                                  size="medium"
                                  color="surface.text.gray.normal"
                                  textAlign="left">
                                  Manual
                                </Text>
                                <Text size="small" color="surface.text.gray.muted" textAlign="left">
                                  You'll get a reminder to trigger payroll yourself.
                                </Text>
                              </Box>
                            </Box>
                          </CardBody>
                        </Card>
                      </Box>
                    </Box>
                  </CardBody>
                </Card>
              </Box>
            )}

            <IsFeatureDisabledForRearch feature="isAdvanceSalaryDisabled">
              <Box marginBottom="spacing.5">
                <Card padding="spacing.7">
                  <CardBody>
                    <Heading
                      size="small"
                      color="surface.text.gray.normal"
                      testID="onboarding-advance-salary-heading">
                      Allow salary advances?
                    </Heading>
                    {/* alignItems="stretch" makes the wrapper Boxes equal-height, and
                        height="100%" on each Card fills its wrapper — both the Card's content
                        border and its selected ring stretch together (no double-stroke artifact). */}
                    <Box display="flex" gap="spacing.7" marginTop="spacing.5" alignItems="stretch">
                      <Box flex={1}>
                        <Card
                          width="100%"
                          height="100%"
                          isSelected={isAdvanceSalaryEnabled === true}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!isFormBeingSubmitted) setIsAdvanceSalaryEnabled(true);
                          }}
                          padding="spacing.7"
                        >
                          <CardBody>
                            <Box display="flex" flexDirection="column" gap="spacing.5">
                              <SettlementsIcon color="surface.icon.gray.normal" size="medium" />
                              <Box display="flex" flexDirection="column" gap="spacing.1">
                                <Text
                                  weight="semibold"
                                  size="medium"
                                  color="surface.text.gray.normal"
                                  textAlign="left">
                                  Yes, enable
                                </Text>
                                <Text size="small" color="surface.text.gray.muted" textAlign="left">
                                  Employees can request via XPayroll. If approved, advance amount
                                  will be paid immediately and recovered from future payments to the
                                  employees.
                                </Text>
                              </Box>
                            </Box>
                          </CardBody>
                        </Card>
                      </Box>
                      <Box flex={1}>
                        <Card
                          width="100%"
                          height="100%"
                          isSelected={isAdvanceSalaryEnabled === false}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!isFormBeingSubmitted) setIsAdvanceSalaryEnabled(false);
                          }}
                          padding="spacing.7"
                        >
                          <CardBody>
                            <Box display="flex" flexDirection="column" gap="spacing.5">
                              <ChevronsRightIcon color="surface.icon.gray.normal" size="medium" />
                              <Box display="flex" flexDirection="column" gap="spacing.1">
                                <Text
                                  weight="semibold"
                                  size="medium"
                                  color="surface.text.gray.normal"
                                  textAlign="left">
                                  Skip for now
                                </Text>
                                <Text size="small" color="surface.text.gray.muted" textAlign="left">
                                  You can enable this anytime from settings.
                                </Text>
                              </Box>
                            </Box>
                          </CardBody>
                        </Card>
                      </Box>
                    </Box>
                  </CardBody>
                </Card>
              </Box>
            </IsFeatureDisabledForRearch>
          </>
        )}

        {!isReArchEmployeeLoansDisabled && (
          <Box display="flex" flexDirection="column" gap="spacing.5">
            {isLoanSettingsError ? (
              <Alert
                color="negative"
                emphasis="subtle"
                isDismissible={false}
                description="Failed to load loan settings. Please refresh and try again."
              />
            ) : (
              <>
                <Heading size="small" weight="semibold" color="surface.text.gray.normal">
                  Loans &amp; Advances
                </Heading>
                <Box
                  marginBottom="spacing.6"
                  borderWidth="thin"
                  borderRadius="large"
                  borderColor="surface.border.gray.muted"
                  backgroundColor="surface.background.gray.intense"
                  padding="spacing.7">
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap="spacing.2"
                    textAlign="left">
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap="spacing.3"
                      textAlign="left"
                      justifyContent="start"
                      alignItems="start">
                      <Text size="small" color="surface.text.gray.muted" textAlign="left">
                        Let employees request loans &amp; advances
                      </Text>
                      <Text
                        size="small"
                        variant="body"
                        weight="medium"
                        color="surface.text.gray.muted"
                        textAlign="left">
                        Employees can submit loan requests through their employee portal. All
                        requests require your approval before processing.
                      </Text>
                    </Box>
                    <Switch
                      accessibilityLabel="Toggle employee loan requests"
                      isChecked={isLoanRequestsEnabled}
                      isDisabled={isLoanSettingsLoading}
                      onChange={({ isChecked }) => setIsLoanRequestsEnabled(isChecked)}
                      size="small"
                    />
                  </Box>
                  {isLoanRequestsEnabled && (
                    <Box
                      borderTopWidth="thin"
                      borderTopColor="surface.border.gray.subtle"
                      marginTop="spacing.5"
                      paddingTop="spacing.4"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between">
                      <Text size="small" color="surface.text.gray.muted">
                        {isLoanCategoriesLoading
                          ? 'Loading categories...'
                          : loanCategoryCount > 0
                          ? `${loanCategoryCount} Loan Categories`
                          : 'Loan Categories'}
                      </Text>
                      <Link size="small" href={routePaths.employeeLoans.settings}>
                        View Categories →
                      </Link>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginTop="auto"
          paddingTop="spacing.6"
          position="sticky"
          bottom="0px"
          paddingY="spacing.5"
          backgroundColor="surface.background.gray.moderate">
          <Link icon={ChevronLeftIcon} iconPosition="left" onClick={handleBackToSetupGuide}>
            Back to setup guide
          </Link>
          <Button
            type="submit"
            variant="primary"
            icon={ArrowRightIcon}
            iconPosition="right"
            isLoading={isFormBeingSubmitted}>
            Save &amp; Next
          </Button>
        </Box>
      </StyledForm>
    </Box>
  );
};

export default PayrollDate;
